'use client';

import { useEffect, useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Banda } from '@/lib/types';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Calendar, AlignLeft, ListMusic, Users, Info, Music2, Search, X, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import Image from '@/components/Image';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';

const DEFAULT_IMAGE = 'https://i.imgur.com/kYkL52G.png';

function hexToRgba(hex: string, alpha: number): string {
  if (!hex || !hex.startsWith('#') || hex.length < 7) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function StatCard({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl bg-muted/40 p-4 text-center">
      <Icon className="h-5 w-5 text-primary" />
      <span className="font-headline text-base font-bold leading-tight">{value}</span>
      <span className="text-[0.65rem] uppercase tracking-wide text-muted-foreground">{label}</span>
    </div>
  );
}

function BandaCard({ banda, onClick }: { banda: Banda; onClick: () => void }) {
  const color = banda.colorPrimario || '#9b8854';
  const colorSec = banda.colorSecundario || color;
  const fotoUrl = banda.foto || DEFAULT_IMAGE;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      whileHover={{ y: -6 }}
      whileTap={{ scale: 0.97 }}
      transition={{ duration: 0.4 }}
      className="group relative cursor-pointer"
      onClick={onClick}
    >
      <div
        className="relative flex h-full flex-col overflow-hidden rounded-2xl border border-white/8 shadow-md transition-all duration-300 group-hover:border-white/20 group-hover:shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
        style={{ background: `linear-gradient(145deg, ${hexToRgba(color, 0.18)} 0%, hsl(var(--card)) 55%)` }}
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[3px] rounded-t-2xl opacity-80 transition-opacity duration-300 group-hover:opacity-100"
          style={{ background: `linear-gradient(90deg, ${color}, ${colorSec})` }}
        />

        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-15 transition-transform duration-700 ease-out group-hover:scale-110 group-hover:opacity-25"
            style={{ backgroundImage: `url('${fotoUrl}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background/95 via-background/60 to-transparent" />
        </div>

        <div className="relative z-10 flex flex-1 flex-col items-center gap-3 p-5 pt-6">
          <div className="relative flex-shrink-0">
            <div
              className="pointer-events-none absolute inset-0 scale-[1.8] rounded-full opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-40"
              style={{ backgroundColor: color }}
            />
            <Image
              src={banda.escudoUrl}
              alt={`Escudo de ${banda.nombreCompleto}`}
              className="relative z-10 h-[76px] w-[76px] object-contain drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] transition-transform duration-400 group-hover:-translate-y-1"
            />
          </div>

          <div className="flex flex-1 flex-col items-center justify-center gap-0.5 text-center">
            <h3 className="font-headline text-[0.95rem] font-bold leading-tight transition-colors duration-300 group-hover:text-primary line-clamp-2">
              {banda.nombreCompleto}
            </h3>
            <p className="mt-0.5 line-clamp-1 text-[0.7rem] text-muted-foreground/60">{banda.tipo}</p>
          </div>

          <div className="mt-1 flex w-full flex-col items-center gap-2">
            <div
              className="h-px w-full opacity-20 transition-opacity duration-300 group-hover:opacity-40"
              style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
            />
            <div className="flex items-center gap-1.5 text-[0.7rem] text-muted-foreground/70">
              <Users className="h-3 w-3 flex-shrink-0" />
              <span>{banda.componentes ?? 0} componentes</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function PublicBandasClient({ initialBandas }: { initialBandas: Banda[] }) {
  const [selectedBanda, setSelectedBanda] = useState<Banda | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'repertorio'>('info');
  const [directorNames, setDirectorNames] = useState<Record<string, string>>({});
  const [repertorioSearch, setRepertorioSearch] = useState('');

  useEffect(() => {
    const allUids = Array.from(new Set(initialBandas.flatMap((b) => b.directores || []).filter(Boolean)));
    if (allUids.length === 0) return;

    const getPublicUserNames = httpsCallable<{ uids: string[] }, { names: Record<string, string> }>(
      functions,
      'getPublicUserNames'
    );
    getPublicUserNames({ uids: allUids })
      .then((result) => setDirectorNames(result.data.names || {}))
      .catch((err) => console.error('Error fetching director names:', err));
  }, [initialBandas]);

  const tiposDeBanda = ['Agrupación Musical', 'Cornetas y Tambores', 'Banda de Música', 'Capilla'];
  const bandasPorTipo = tiposDeBanda
    .map((tipo) => ({ tipo, bandas: initialBandas.filter((b) => b.tipo === tipo) }))
    .filter((g) => g.bandas.length > 0);

  const color = selectedBanda?.colorPrimario || '#9b8854';
  const colorSec = selectedBanda?.colorSecundario || color;
  const fotoUrl = selectedBanda?.foto || DEFAULT_IMAGE;

  const sortedRepertorio = useMemo(() => {
    if (!selectedBanda?.repertorio) return [];
    return [...selectedBanda.repertorio].sort((a, b) => a.localeCompare(b));
  }, [selectedBanda]);

  const filteredRepertorio = useMemo(() => {
    const term = repertorioSearch.trim().toLowerCase();
    if (!term) return sortedRepertorio;
    return sortedRepertorio.filter((marcha) => marcha.toLowerCase().includes(term));
  }, [sortedRepertorio, repertorioSearch]);

  const tabs = [
    { id: 'info' as const, label: 'Info', icon: Info },
    { id: 'repertorio' as const, label: 'Repertorio', icon: ListMusic },
  ];

  return (
    <div className="min-h-screen bg-background">
      <section className="relative overflow-hidden border-b border-white/8 py-20 text-center">
        <div className="absolute -top-32 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/10 blur-[120px]" />
        <div className="container relative mx-auto max-w-3xl px-4">
          <motion.div
            className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-primary/30 bg-primary/10"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Music2 className="h-7 w-7 text-primary" />
          </motion.div>
          <motion.h1
            className="font-headline text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="bg-gradient-to-r from-primary to-amber-500 bg-clip-text text-transparent">Nuestras Bandas</span>
          </motion.h1>
          <motion.p
            className="mt-4 text-base text-muted-foreground md:text-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            El alma sonora de Jartiland. Descubre las formaciones musicales que ponen la banda sonora a nuestras procesiones y eventos.
          </motion.p>
        </div>
      </section>

      <section className="container mx-auto space-y-16 px-4 py-16">
        {bandasPorTipo.map(({ tipo, bandas }) => (
          <div key={tipo}>
            <div className="mb-8 flex items-center gap-4">
              <h2 className="font-headline text-3xl font-bold md:text-4xl">{tipo}</h2>
              <div className="h-px flex-1 bg-gradient-to-r from-primary/40 to-transparent" />
            </div>
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {bandas.map((banda) => (
                <BandaCard
                  key={banda.id}
                  banda={banda}
                  onClick={() => {
                    setSelectedBanda(banda);
                    setActiveTab('info');
                    setRepertorioSearch('');
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </section>

      <Sheet open={!!selectedBanda} onOpenChange={(open) => !open && setSelectedBanda(null)}>
        <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
          {selectedBanda && (
            <>
              <div className="relative flex min-h-[220px] flex-shrink-0 flex-col justify-end overflow-hidden">
                <div className="absolute inset-0">
                  <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${fotoUrl}')` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{ background: `linear-gradient(145deg, ${color} 0%, transparent 55%)` }}
                  />
                </div>

                <div className="relative z-10 flex flex-col items-center gap-3 px-6 pb-5 pt-10 text-center">
                  <div className="relative">
                    <div
                      className="absolute inset-0 scale-[2.2] rounded-full opacity-35 blur-2xl"
                      style={{ backgroundColor: color }}
                    />
                    <div
                      className="relative rounded-full border-2 p-2.5 backdrop-blur-sm"
                      style={{ borderColor: hexToRgba(color, 0.45), backgroundColor: hexToRgba(color, 0.2) }}
                    >
                      <Image
                        src={selectedBanda.escudoUrl}
                        alt={`Escudo de ${selectedBanda.nombreCompleto}`}
                        className="h-[88px] w-[88px] object-contain drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]"
                      />
                    </div>
                  </div>
                  <SheetHeader className="m-0 p-0">
                    <SheetTitle className="font-headline text-xl font-bold">{selectedBanda.nombreCompleto}</SheetTitle>
                    <SheetDescription className="text-sm text-muted-foreground/70">{selectedBanda.tipo}</SheetDescription>
                  </SheetHeader>
                </div>

                <div className="relative z-10 flex h-[3px] flex-shrink-0">
                  <div style={{ backgroundColor: color, flex: 1 }} />
                  <div style={{ backgroundColor: colorSec, flex: 1 }} />
                </div>
              </div>

              <div className="flex flex-shrink-0 items-center gap-2 border-b border-white/5 bg-background/60 px-3 py-2 backdrop-blur-sm">
                <TooltipProvider delayDuration={300}>
                  <div className="flex gap-0.5">
                    {tabs.map(({ id, label, icon: Icon }) => {
                      const isActive = activeTab === id;
                      return (
                        <Tooltip key={id}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => setActiveTab(id)}
                              className={cn(
                                'flex h-9 w-9 items-center justify-center rounded-xl transition-all duration-200',
                                isActive ? 'text-white shadow-md' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                              )}
                              style={isActive ? { backgroundColor: color } : {}}
                            >
                              <Icon className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="text-xs">
                            {label}
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </TooltipProvider>

                {/* Ficha pública completa, con el repertorio escuchable */}
                <Link
                  to={`/bandas/${selectedBanda.id}`}
                  className="ml-auto flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-xs font-semibold transition-colors hover:bg-white/10"
                  style={{ color }}
                >
                  Ver ficha completa
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>

              <ScrollArea className="relative flex-1">
                <div
                  className="absolute inset-0 opacity-[0.04]"
                  style={{ backgroundImage: `url(${selectedBanda.escudoUrl})`, backgroundSize: '120px', backgroundRepeat: 'repeat' }}
                />
                <div className="relative z-10 space-y-6 p-6">
                  {activeTab === 'info' && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <StatCard icon={Calendar} label="Fundación" value={selectedBanda.fundacion} />
                        <StatCard icon={Users} label="Componentes" value={selectedBanda.componentes ?? '—'} />
                        <StatCard icon={ListMusic} label="Marchas" value={selectedBanda.repertorio?.length || 0} />
                      </div>

                      {selectedBanda.directores && selectedBanda.directores.length > 0 && (
                        <div className="rounded-2xl bg-muted/40 p-4">
                          <h4 className="mb-2 text-sm font-semibold">Directores</h4>
                          <div className="flex flex-wrap gap-2">
                            {selectedBanda.directores.map((director) => (
                              <span
                                key={director}
                                className="rounded-full border px-2.5 py-1 text-xs font-medium"
                                style={{ borderColor: hexToRgba(color, 0.4), backgroundColor: hexToRgba(color, 0.12) }}
                              >
                                {directorNames[director] || 'Cargando...'}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedBanda.info && (
                        <>
                          <div className="flex h-[2px] w-full overflow-hidden rounded-full">
                            <div style={{ backgroundColor: color }} className="flex-1" />
                            <div style={{ backgroundColor: colorSec }} className="flex-1" />
                          </div>
                          <div className="flex items-start gap-3">
                            <AlignLeft className="mt-1 h-5 w-5 flex-shrink-0 text-muted-foreground" />
                            <div className="prose prose-sm prose-invert max-w-none text-muted-foreground">
                              <ReactMarkdown>{selectedBanda.info}</ReactMarkdown>
                            </div>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {activeTab === 'repertorio' && (
                    <div className="space-y-4">
                      <h3 className="flex items-center gap-2 font-headline text-lg font-semibold">
                        <ListMusic className="h-5 w-5 text-primary" />
                        Repertorio Musical ({sortedRepertorio.length} marchas)
                      </h3>

                      {sortedRepertorio.length > 0 && (
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <input
                            value={repertorioSearch}
                            onChange={(e) => setRepertorioSearch(e.target.value)}
                            placeholder="Buscar marcha..."
                            className="w-full rounded-xl border border-white/10 bg-muted/40 py-2 pl-9 pr-9 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary/50 focus:outline-none"
                          />
                          {repertorioSearch && (
                            <button
                              onClick={() => setRepertorioSearch('')}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-primary"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      )}

                      {sortedRepertorio.length > 0 ? (
                        filteredRepertorio.length > 0 ? (
                          <ul className="space-y-1.5">
                            {filteredRepertorio.map((marcha, index) => (
                              <li
                                key={index}
                                className="rounded-xl bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
                              >
                                {marcha}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-sm text-muted-foreground">Ninguna marcha coincide con tu búsqueda.</p>
                        )
                      ) : (
                        <p className="text-sm text-muted-foreground">Esta banda todavía no ha definido su repertorio.</p>
                      )}
                    </div>
                  )}
                </div>
              </ScrollArea>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
